// Dreamstolife3D customisations on top of stock Horizon.
import { ThemeEvents } from '@theme/events';

/* Dialogs opened by [data-dtl-dialog-open="<dialog id>"] */
document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  const opener = target.closest('[data-dtl-dialog-open]');
  if (opener instanceof HTMLElement) {
    const dialog = document.getElementById(opener.dataset.dtlDialogOpen || '');
    if (dialog instanceof HTMLDialogElement) dialog.showModal();
    return;
  }
  if (target.closest('[data-dtl-dialog-close]')) {
    target.closest('dialog')?.close();
    return;
  }
  // Click on the backdrop closes the dialog
  if (target instanceof HTMLDialogElement && target.classList.contains('dtl-dialog')) target.close();
});

/* Request block: only visible while the selected variant is sold out */
document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const variant = /** @type {any} */ (event).detail?.resource;
  if (!variant) return;
  const section = /** @type {HTMLElement} */ (event.target).closest('.shopify-section') || document;
  section.querySelectorAll('[data-dtl-request]').forEach((block) => {
    block.toggleAttribute('hidden', Boolean(variant.available));
    const field = block.querySelector('[data-dtl-request-product]');
    if (field instanceof HTMLInputElement) {
      const [title] = field.value.split(' | ');
      field.value = `${title} | ${variant.title} | ${location.origin}${location.pathname}?variant=${variant.id}`;
    }
    const label = block.querySelector('[data-dtl-variant-title]');
    if (label) label.textContent = variant.title;
    const paint = block.querySelector('[data-dtl-paint-options]');
    if (paint instanceof HTMLFieldSetElement) {
      const painted = /paint|pict/i.test(variant.title || '');
      paint.hidden = !painted;
      paint.disabled = !painted;
    }
    syncConsent(block);
  });
});

/* Request consent: show the statement matching the chosen painting option and send it with the form */
function syncConsent(/** @type {Element} */ scope) {
  const paint = scope.querySelector('[data-dtl-paint-options]');
  const choice = paint instanceof HTMLFieldSetElement && !paint.disabled
    ? paint.querySelector('input[type="radio"]:checked')?.getAttribute('data-dtl-kind')
    : 'standard';
  scope.querySelectorAll('[data-dtl-consent]').forEach((el) => {
    if (el instanceof HTMLElement) el.hidden = el.dataset.dtlConsent !== (choice || 'standard');
  });
  const visible = scope.querySelector('[data-dtl-consent]:not([hidden])');
  const value = scope.querySelector('[data-dtl-consent-value]');
  if (value instanceof HTMLInputElement) value.value = visible?.textContent?.trim() || '';
}
document.addEventListener('change', (event) => {
  const scope = /** @type {Element} */ (event.target).closest('[data-dtl-request]');
  if (scope) syncConsent(scope);
});
document.querySelectorAll('[data-dtl-request]').forEach(syncConsent);

/* Contact page (anti-spam, started from the original pre-redesign form):
   - the guide template stays in the message box as real text; each "Label:" line needs a short answer
   - Send stays disabled until email/phone are valid and every template field is answered
   - anti-bot: hidden honeypot field + minimum time on page; anti-repeat: cooldown after a sent message
     and the same message cannot be sent twice (per browser; Shopify's hCaptcha covers bots posting directly)
   - prefill from ?topic=international and ?product= */
function initContactForm() {
  const form = document.querySelector('.contact-form__form');
  if (!(form instanceof HTMLFormElement)) return;
  const email = form.querySelector('[name="contact[email]"]');
  const phone = form.querySelector('[name="contact[phone]"]');
  const body = form.querySelector('[name="contact[body]"]');
  const honeypot = form.querySelector('.dtl-hp input');
  const submit = form.querySelector('[type="submit"]');
  if (!(email instanceof HTMLInputElement) || !(body instanceof HTMLTextAreaElement)) return;
  let i18n = {};
  try { i18n = JSON.parse(document.getElementById('dtl-contact-i18n')?.textContent || '{}'); } catch {}
  const t = (key, fallback) => i18n[key] || fallback;

  const MIN_MS = 5000;            // a person needs more than 5 s to fill the form; bots usually don't wait
  const COOLDOWN_MS = 10 * 60e3;  // one message per 10 minutes from the same browser
  const SAME_MS = 24 * 3600e3;    // the identical message cannot be sent again for 24 h
  const started = Date.now();
  const store = {
    get(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
    sget(k) { try { return sessionStorage.getItem(k); } catch { return null; } },
    sset(k, v) { try { v === null ? sessionStorage.removeItem(k) : sessionStorage.setItem(k, v); } catch {} },
  };
  const norm = (v) => v.replace(/\s+/g, ' ').trim().toLowerCase();
  const hash = (v) => { let h = 0; for (const c of norm(v)) h = (h * 31 + c.charCodeAt(0)) | 0; return String(h); };

  // Message sent successfully (Shopify re-renders the page with the success box): start the cooldown
  const success = form.querySelector('.contact-form__success');
  if (success) {
    const pending = store.sget('dtl-contact-pending');
    const sent = (store.get('dtl-contact-sent') || []).filter((x) => Date.now() - x.t < SAME_MS);
    sent.push({ t: Date.now(), h: pending || '' });
    store.set('dtl-contact-sent', sent);
    store.sset('dtl-contact-pending', null);
    success.scrollIntoView({ block: 'center' });
  }

  // Structured request: fixed labels (not editable), the customer only types the answers.
  // The answers are written into the real message box (contact[body]) as "Label: answer" lines.
  const cf = form.querySelector('.dtl-cf');
  const params = new URLSearchParams(location.search);
  const product = params.get('product');
  const intl = params.get('topic') === 'international' ? (form.dataset.dtlIntlLabel || 'Shipping outside Romania: ') : '';
  const field = (name) => /** @type {HTMLTextAreaElement|HTMLInputElement|null} */ (cf?.querySelector(`[data-field="${name}"]`));
  const labelOf = (el) => (el?.closest('.dtl-cf__row')?.querySelector('.dtl-cf__label')?.textContent || '').trim().replace(/:$/, '');
  const type = () => /** @type {HTMLInputElement|null} */ (cf?.querySelector('[name="dtl_type"]:checked'))?.value || 'custom';
  const choice = (name) => /** @type {HTMLInputElement|null} */ (cf?.querySelector(`[name="dtl_${name}"]:checked`));
  // Size in mm: largest number written ("100", "120 x 80 x 200"); above 350 mm the parts/price disclaimer is highlighted
  const sizeMm = () => { const n = ((field('size')?.value || '').replace(/,/g, '.').match(/\d+(\.\d+)?/g) || []).map(Number); return n.length ? Math.max(...n) : NaN; };
  const linkOk = () => { const v = (field('link')?.value || '').trim(); return v === '' || /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(\S*)$/i.test(v); };
  const answered = (v = '') => (v.match(/[\p{L}\p{N}]/gu) || []).length >= 3;
  // Grow only while visible (a hidden box has scrollHeight 0 and would collapse to 0 px)
  const grow = (el) => { if (el instanceof HTMLTextAreaElement && el.offsetParent) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } };

  if (cf instanceof HTMLElement) {
    cf.hidden = false;
    body.hidden = true;
    body.closest('.contact-form__form-row, label')?.classList.add('dtl-cf-replaced');
    if (product && field('object')) field('object').value = product;
    if (intl) {
      /** @type {HTMLInputElement} */ (cf.querySelector('[name="dtl_type"][value="other"]')).checked = true;
      field('message').value = intl;
    }
    cf.querySelectorAll('textarea').forEach((el) => { grow(el); el.addEventListener('input', () => grow(el)); });
  }
  const compose = () => {
    if (!(cf instanceof HTMLElement)) return;
    const typeLabel = cf.querySelector('.dtl-cf__types')?.getAttribute('data-label') || 'Request type';
    const typeText = cf.querySelector('[name="dtl_type"]:checked + span')?.textContent?.trim() || '';
    const lines = [`${typeLabel}: ${typeText}`];
    if (type() === 'custom') {
      for (const name of ['object', 'size', 'finish', 'use', 'link', 'details']) {
        let v = name === 'finish' || name === 'use' ? (choice(name)?.nextElementSibling?.textContent || '') : (field(name)?.value || '');
        v = v.trim();
        if (name === 'size' && v && !/mm/i.test(v)) v = `${v} mm`;
        lines.push(`${labelOf(field(name))}: ${v || '—'}`);
      }
    } else {
      lines.push(field('message')?.value.trim() || '');
    }
    body.value = lines.join('\n');
    cf.querySelectorAll('.dtl-cf__box').forEach((box) => {
      const show = box.getAttribute('data-type') === type();
      if (/** @type {HTMLElement} */ (box).hidden === show) {
        /** @type {HTMLElement} */ (box).hidden = !show;
        if (show) box.querySelectorAll('textarea').forEach(grow);
      }
    });
  };
  // What is still missing, by label (shown under the form)
  const missing = () => {
    if (!(cf instanceof HTMLElement)) return answered(body.value) ? [] : ['…'];
    if (type() === 'other') {
      const own = (field('message')?.value || '').replace(intl.trim(), '').replace(/\s+/g, ' ').trim();
      return own.length >= 20 ? [] : [t('message_min', 'Message (at least 20 characters)')];
    }
    const out = [];
    if (!answered(field('object')?.value)) out.push(labelOf(field('object')));
    if (!(sizeMm() > 0)) out.push(labelOf(field('size')));
    if (!choice('finish')) out.push(labelOf(field('finish')));
    if (!choice('use')) out.push(labelOf(field('use')));
    if (!linkOk()) out.push(t('link_invalid', "The reference link doesn't look valid."));
    return out;
  };
  const markFields = () => {
    if (!(cf instanceof HTMLElement)) return;
    const mm = sizeMm();
    cf.querySelector('[data-note="size"]')?.classList.toggle('dtl-cf__note--warn', mm > 350);
    const state = { object: answered(field('object')?.value), size: mm > 0, link: linkOk(), message: missing().length === 0 };
    for (const [name, ok] of Object.entries(state)) {
      const el = field(name); if (!el) continue;
      const empty = el.value.trim() === '';
      el.closest('.dtl-cf__row')?.classList.toggle('dtl-cf__row--bad', !empty && !ok);
      el.closest('.dtl-cf__row')?.classList.toggle('dtl-cf__row--ok', !empty && ok);
    }
  };
  compose();

  const status = document.createElement('p');
  status.className = 'dtl-contact-status';
  status.setAttribute('aria-live', 'polite');
  // Under the structured request when it is shown, otherwise under the plain message box
  (cf instanceof HTMLElement && !cf.hidden ? cf : body).insertAdjacentElement('afterend', status);

  const blocked = () => {
    const sent = (store.get('dtl-contact-sent') || []).filter((x) => Date.now() - x.t < SAME_MS);
    const last = sent.length ? Math.max(...sent.map((x) => x.t)) : 0;
    const wait = last + COOLDOWN_MS - Date.now();
    if (wait > 0) return t('cooldown', 'You already sent a message. You can send another one in {min} min.').replace('{min}', String(Math.ceil(wait / 60e3)));
    if (sent.some((x) => x.h && x.h === hash(body.value))) return t('duplicate', 'This message has already been sent.');
    return '';
  };

  const rules = [
    [email, () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())],
    [phone, () => !(phone instanceof HTMLInputElement) || phone.value.trim() === '' || /^\+?[0-9 ()-]{9,16}$/.test(phone.value.trim())],
    [body, () => missing().length === 0],
  ];

  function update() {
    compose();
    markFields();
    let ok = true;
    for (const [input, valid] of rules) {
      if (!(input instanceof HTMLElement) || input.hidden) { ok &&= valid(); continue; }
      const v = /** @type {HTMLInputElement} */ (input).value.trim();
      const empty = v === '';
      const isValid = valid();
      ok &&= isValid;
      input.classList.toggle('dtl-valid', !empty && isValid);
      input.classList.toggle('dtl-invalid', !empty && !isValid);
    }
    const bot = honeypot instanceof HTMLInputElement && honeypot.value !== '';
    const tooFast = Date.now() - started < MIN_MS;
    const stop = blocked();
    const todo = missing();
    ok = ok && !bot && !tooFast && !stop;
    if (submit instanceof HTMLButtonElement) submit.disabled = !ok;
    status.textContent = stop || (todo.length ? `${t('todo', 'Still to fill in:')} ${todo.map((x) => x.replace(/\.$/, '')).join(', ')}` : t('ready', 'Ready to send ✓'));
    status.classList.toggle('dtl-contact-status--ok', !stop && !todo.length);
    status.classList.toggle('dtl-contact-status--stop', Boolean(stop));
  }
  form.addEventListener('input', update);
  form.addEventListener('change', update);
  form.addEventListener('submit', (e) => {
    update();
    if (submit instanceof HTMLButtonElement && submit.disabled) { e.preventDefault(); return; }
    store.sset('dtl-contact-pending', hash(body.value));
  });
  setTimeout(update, MIN_MS + 50);
  update();
}

/* Account popover: add a Sign out link (not provided by the shopify-account component) */
function injectLogout() {
  const account = document.querySelector('shopify-account');
  const nav = account?.shadowRoot?.querySelector('nav.stack-inline');
  if (!nav || nav.querySelector('.dtl-logout')) return Boolean(nav);
  const link = document.createElement('a');
  link.href = '/account/logout';
  link.target = '_top';
  link.className = 'button outline dtl-logout';
  link.textContent = document.documentElement.lang.startsWith('ro') ? 'Deconectare' : 'Sign out';
  nav.appendChild(link);
  return true;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initContactForm, { once: true });
} else {
  initContactForm();
}
if (document.querySelector('shopify-account') && !injectLogout()) {
  const observer = new MutationObserver(() => injectLogout() && observer.disconnect());
  observer.observe(document.body, { childList: true, subtree: true });
}

/* Menu shortcut: a link ending in "#meniu" (e.g. the hero "Vezi figurinele" button) opens the
   header menu item that points to the same page instead of navigating. Without JS it simply
   navigates to that page. Desktop: opens the dropdown. Mobile: opens the drawer with that group expanded.
   Shop rule: visitors browse only through the menu, never the "all products" list. So any link to
   /collections/all outside the menu (empty collection, empty cart, 404, buttons added later in Customize)
   opens the menu too: the group holding the current collection, otherwise the main figurines group. */
const DTL_MENU_DEFAULT = '/collections/toate-figurinele';
const dtlPath = (/** @type {string} */ href) => new URL(href, location.origin).pathname.replace(/^\/[a-z]{2}(?=\/)/, '');
document.addEventListener('click', (event) => {
  const trigger = /** @type {HTMLElement} */ (event.target).closest('a[href]');
  if (!(trigger instanceof HTMLAnchorElement)) return;
  if (trigger.closest('.header, header-drawer, .menu-drawer')) return; // the menu itself navigates normally
  const target = new URL(trigger.href, location.origin);
  const isShortcut = target.hash === '#meniu';
  if (!isShortcut && !/^\/collections\/all\/?$/.test(dtlPath(target.href))) return;
  const headerLinks = [...document.querySelectorAll('.header .menu-list__link')].filter((a) => a instanceof HTMLAnchorElement);
  let path = dtlPath(target.href);
  if (!isShortcut) {
    // "all products": open the menu group that contains the page we are on, else the default group
    const here = dtlPath(location.href);
    const parent = headerLinks.find((a) =>
      [...(a.closest('.menu-list__list-item')?.querySelectorAll('.menu-list__submenu a[href]') ?? [])].some(
        (s) => dtlPath(/** @type {HTMLAnchorElement} */ (s).href) === here
      )
    );
    path = parent ? dtlPath(/** @type {HTMLAnchorElement} */ (parent).href) : DTL_MENU_DEFAULT;
  }
  const headerLink = headerLinks.find((a) => dtlPath(/** @type {HTMLAnchorElement} */ (a).href) === path);
  if (!headerLink) return; // no matching menu item: let the link navigate
  event.preventDefault();
  window.scrollTo({ top: 0, behavior: 'smooth' });

  const drawer = document.querySelector('header-drawer details.menu-drawer-container');
  const isDrawerMode = drawer instanceof HTMLDetailsElement && drawer.offsetParent !== null;
  if (!isDrawerMode) {
    const item = headerLink.closest('.menu-list__list-item');
    item?.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
    headerLink.focus({ preventScroll: true });
    return;
  }
  const title = headerLink.textContent?.trim().toLowerCase();
  drawer.querySelector('summary')?.click();
  setTimeout(() => {
    const groups = [...drawer.querySelectorAll('details details')];
    const group = groups.find(
      (d) => d.querySelector('.menu-drawer__menu-item-text')?.textContent?.trim().toLowerCase() === title
    );
    // only the target group stays expanded, so it is the first thing the visitor sees
    for (const other of groups) if (other !== group && other.open) other.querySelector('summary')?.click();
    if (group && !group.open) group.querySelector('summary')?.click();
  }, 350);
});

/* Header dropdowns: a top-level item with a submenu only opens it (hover or click), it never navigates.
   The panel stays open while the pointer is over the item or its panel, or until a sub-item is chosen. */
document.addEventListener('click', (event) => {
  const link = /** @type {HTMLElement} */ (event.target).closest('.header .menu-list__link[aria-haspopup="true"]');
  if (!(link instanceof HTMLAnchorElement)) return;
  event.preventDefault();
  const item = link.closest('.menu-list__list-item');
  if (link.getAttribute('aria-expanded') !== 'true') {
    item?.dispatchEvent(Object.assign(new PointerEvent('pointerenter', { bubbles: false }), { dtlIntent: true }));
  }
  link.focus({ preventScroll: true });
});

/* Product page: "← Back to <collection>" returns to the collection the visitor came from */
(() => {
  const back = document.querySelector('[data-dtl-back]');
  if (!(back instanceof HTMLAnchorElement) || !document.referrer) return;
  let ref;
  try { ref = new URL(document.referrer); } catch { return; }
  if (ref.origin !== location.origin) return;
  const match = ref.pathname.match(/\/collections\/([^/?#]+)/);
  if (!match) return;
  const collections = JSON.parse(back.dataset.collections || '{}');
  const title = collections[match[1]];
  if (!title) return;
  back.href = ref.pathname + ref.search;
  const text = back.querySelector('[data-dtl-back-text]');
  if (text) text.textContent = (back.dataset.labelTemplate || '').replace('__C__', title);
  back.addEventListener('click', (event) => {
    if (history.length > 1) { event.preventDefault(); history.back(); }
  });
})();

/* Header dropdown hover intent (desktop): while a panel is open, passing over another category on the
   way to the panel does not switch it (listeners on window run before Horizon's document-level ones); the pointer has to rest on that category for a moment.
*/
(() => {
  const SWITCH_DELAY = 220;
  const lastPointer = { x: 0, y: 0 };
  document.addEventListener('pointermove', (e) => { lastPointer.x = e.clientX; lastPointer.y = e.clientY; }, { passive: true });
  let pending = 0;
  const openItem = () => document.querySelector('.header .menu-list__link[aria-expanded="true"]')?.closest('.menu-list__list-item');
  window.addEventListener('pointerenter', (event) => {
    const item = event.target;
    if (!(item instanceof HTMLElement) || !item.matches('.header .menu-list__list-item')) return;
    if (/** @type {any} */ (event).dtlIntent) return; // our own delayed re-dispatch
    const current = openItem();
    if (!current || current === item) return;
    event.stopImmediatePropagation(); // keep the current panel for now
    clearTimeout(pending);
    pending = window.setTimeout(() => {
      // switch only if the pointer is really resting on that category's button row
      const under = document.elementFromPoint(lastPointer.x, lastPointer.y);
      const onButtonRow = under?.closest('.menu-list__list-item') === item && !under.closest('.menu-list__submenu');
      if (onButtonRow) item.dispatchEvent(Object.assign(new PointerEvent('pointerenter', { bubbles: false }), { dtlIntent: true }));
    }, SWITCH_DELAY);
  }, true);
  // Close grace: leaving the category (e.g. through the small gap above the panel) only closes it
  // if the pointer is still outside the category and its panel after CLOSE_DELAY.
  const CLOSE_DELAY = 300;
  window.addEventListener('pointerleave', (event) => {
    const item = event.target;
    if (!(item instanceof HTMLElement) || !item.matches('.header .menu-list__list-item')) return;
    clearTimeout(pending);
    if (/** @type {any} */ (event).dtlIntent) return; // our own delayed re-dispatch
    event.stopImmediatePropagation();
    // Horizon's deactivate closes whichever panel is open, so only the open category may close it.
    if (openItem() !== item) return;
    window.setTimeout(() => {
      if (openItem() === item && !item.matches(':hover')) {
        item.dispatchEvent(Object.assign(new PointerEvent('pointerleave', { bubbles: false }), { dtlIntent: true }));
      }
    }, CLOSE_DELAY);
  }, true);
})();

/* Crash guard (Chrome 156 renderer CHECK): after clicking a category (focus on its link), pressing on an empty
   spot of the open panel blurred the link; Horizon then closed the panel under the pressed pointer, which crashed
   Chrome ("Aw, Snap"). 1) pressing on empty panel space no longer moves focus; 2) a blur while the pointer is still
   over the category/panel does not close it. */
document.addEventListener('pointerdown', (event) => {
  const target = /** @type {Element} */ (event.target);
  if (!target.closest?.('.header .menu-list__submenu')) return;
  if (target.closest('a, button, input, select, textarea, [tabindex]')) return;
  event.preventDefault();
}, true);
window.addEventListener('blur', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const item = target.closest('.header .menu-list__list-item');
  if (item && item.matches(':hover')) event.stopImmediatePropagation();
}, true);
